import { ActionIcon, Flex, Menu, Text, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconDots, IconEdit, IconScissors, IconSwitchHorizontal, IconTrash } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'
import { useVariants } from 'hooks/products'
import { useDeleteVariant } from 'hooks/variants'
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_TableOptions,
  useMantineReactTable,
} from 'mantine-react-table'
import { MRT_Localization_PL } from 'mantine-react-table/locales/pl/index.cjs'
import { useMemo, useState } from 'react'
import {
  Category,
  FabricVariant,
  PillowVariant,
  PremadeVariant,
  TableVariant,
  TowelVariant,
  Unit,
  Variant,
} from 'types/products'

export type VariantWithProduct<V extends Variant> = V & { product: { id: string; symbol: string } }

type VariantMap = {
  [Category.FABRIC]: VariantWithProduct<FabricVariant>
  [Category.PREMADE]: VariantWithProduct<PremadeVariant>
  [Category.PILLOW]: VariantWithProduct<PillowVariant>
  [Category.TOWEL]: VariantWithProduct<TowelVariant>
  [Category.TABLE]: VariantWithProduct<TableVariant>
}

interface BaseProps<C extends Category> extends Omit<MRT_TableOptions<VariantMap[C]>, 'data'> {
  category: C
}

type Props<C extends Category> = BaseProps<C>

export function useTableWithProduct<C extends Category>({ category, columns }: Props<C>) {
  const navigate = useNavigate()
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  })

  const { data, isLoading, isError, error, isFetching } = useVariants<VariantMap[C]>({
    category,
    columnFilters,
    sorting,
    pagination,
  })

  const memoizedData = useMemo(() => data ?? { data: [], total: 0 }, [JSON.stringify(data)])

  const deleteVariant = useDeleteVariant()

  function openDeleteModal(variantId: string) {
    const variant = memoizedData.data.find((v) => v.id === variantId)
    if (!variant) return
    modals.openConfirmModal({
      title: 'Usuń produkt',
      centered: true,
      children: (
        <Text size="sm">
          Czy na pewno chcesz usunąć <b>{variant.stock}</b>{' '}
          {variant.unit === Unit.METER ? 'metrów' : variant.unit === Unit.CENTIMETER ? 'centymetrów' : 'sztuk'} produktu
          o {variant.unit === Unit.PIECE ? 'wymiarze' : 'szerokości'} <b>{variant.size}</b>{' '}
          {variant.unit === Unit.PIECE ? '' : 'cm'} z półki{' '}
          <b>
            {variant.place.rack}-{variant.place.column}-{variant.place.shelf}
          </b>
          ? Nie będzie można cofnąć tej operacji.
        </Text>
      ),
      labels: { confirm: 'Usuń produkt', cancel: 'Nie chcę usuwać' },
      confirmProps: { color: 'red' },
      onConfirm: async () => await deleteVariant.mutateAsync(variant),
    })
  }

  function openTakeModal(variantId: string) {
    const variant = memoizedData.data.find((v) => v.id === variantId)
    if (!variant) return
    modals.openContextModal({
      modal: 'takeProduct',
      centered: true,
      title: 'Pobierz produkt z magazynu',
      innerProps: {
        variant: variant,
      },
    })
  }

  function openMovePlaceModal(variantId: string) {
    const variant = memoizedData.data.find((v) => v.id === variantId)
    if (!variant) return
    modals.openContextModal({
      modal: 'movePlace',
      centered: true,
      title: 'Przenieś produkt na inną półkę',
      innerProps: {
        variant: variant,
      },
    })
  }

  function openEditVariantModal(variantId: string) {
    const variant = memoizedData.data.find((v) => v.id === variantId)
    if (!variant) return

    const properties = {
      modal: 'editVariant',
      centered: true,
      title: 'Edytuj produkt',
    } as const

    if (variant.category === Category.FABRIC) {
      modals.openContextModal({
        ...properties,
        innerProps: {
          variant: variant as FabricVariant,
          category: Category.FABRIC,
        },
      })
    } else if (variant.category === Category.PREMADE) {
      modals.openContextModal({
        ...properties,
        innerProps: {
          variant: variant as PremadeVariant,
          category: Category.PREMADE,
        },
      })
    } else if (variant.category === Category.PILLOW) {
      modals.openContextModal({
        ...properties,
        innerProps: {
          variant: variant as PillowVariant,
          category: Category.PILLOW,
        },
      })
    } else if (variant.category === Category.TOWEL) {
      modals.openContextModal({
        ...properties,
        innerProps: {
          variant: variant as TowelVariant,
          category: Category.TOWEL,
        },
      })
    } else if (variant.category === Category.TABLE) {
      modals.openContextModal({
        ...properties,
        innerProps: {
          variant: variant as TableVariant,
          category: Category.TABLE,
        },
      })
    }
  }

  const onRowClick = (variant: Variant) => {
    navigate({ to: `/product/${variant.productId}`, search: { variant: variant.id } })
  }

  const pageSize = pagination.pageSize

  const table = useMantineReactTable({
    columns,
    data: memoizedData.data,
    rowCount: memoizedData.total,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: {
      columnFilters,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isFetching,
      sorting,
    },
    mantineToolbarAlertBannerProps: isError
      ? {
          color: 'red',
          children: `Wystąpił błąd podczas pobierania danych. (${error})`,
        }
      : undefined,
    autoResetPageIndex: false,
    layoutMode: 'grid',
    enableColumnActions: false,
    enableColumnFilters: true,
    enableTopToolbar: false,
    enableHiding: false,
    enableDensityToggle: false,
    enableGlobalFilter: false,
    enableBottomToolbar: memoizedData.total < pageSize + 1 ? false : true,
    enableFullScreenToggle: false,
    paginationDisplayMode: 'pages',
    localization: MRT_Localization_PL,
    enableRowActions: true,
    positionActionsColumn: 'last',
    mantinePaginationProps: {
      showRowsPerPage: false,
      rowsPerPageOptions: [pageSize ? pageSize.toString() : '5'],
    },
    initialState: {
      pagination: { pageIndex: 0, pageSize: 5 },
      showColumnFilters: true,
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        grow: false,
        size: 100,
      },
    },
    mantinePaperProps: {
      style: {
        borderTop: 0,
      },
    },
    mantineTableBodyCellProps: ({ row, cell }) => {
      if (!cell.id.endsWith('mrt-row-actions')) {
        return {
          style: {
            cursor: 'pointer',
          },
          onClick: () => onRowClick?.(row.original),
        }
      } else {
        return {}
      }
    },
    renderRowActions: ({ row }) => (
      <Flex gap="sm" justify="end">
        <Tooltip label="Pobierz">
          <ActionIcon radius="xl" color="brand" onClick={() => openTakeModal(row.original.id)}>
            <IconScissors size={18} />
          </ActionIcon>
        </Tooltip>
        <Menu withArrow arrowSize={10} position="bottom-end" withinPortal offset={0} arrowOffset={8}>
          <Menu.Target>
            <ActionIcon radius="xl" variant="subtle" color="gray">
              <IconDots size={18} stroke={2} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconSwitchHorizontal size={16} stroke={1.5} />}
              onClick={() => openMovePlaceModal(row.original.id)}
            >
              Przenieś
            </Menu.Item>
            <Menu.Item
              leftSection={<IconEdit size={16} stroke={1.5} />}
              onClick={() => openEditVariantModal(row.original.id)}
            >
              Edytuj
            </Menu.Item>
            <Menu.Item
              leftSection={<IconTrash size={16} stroke={1.5} />}
              color="red"
              onClick={() => openDeleteModal(row.original.id)}
            >
              Usuń
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Flex>
    ),
  })

  return table
}
